import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabaseClient';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { exportToProfessionalCSV } from '../../utils/csvExport';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { useData } from '../../context/DataContext';
import { formatDate } from '../../utils/formatters';
import { usePreferences } from '../../hooks/usePreferences';

const AuditReports = () => {
    const { currentUser } = useData();
    const canDelete = currentUser?.role === 'admin' || currentUser?.role === 'system_admin';
    const [departments, setDepartments] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);

    const [activeDetailTab, setActiveDetailTab] = useState('all');
    const [detailSearch, setDetailSearch] = useState('');

    const { preferences } = usePreferences();
    const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

    const addNotification = (message, type) => {
        setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
    };

    const filteredDetailAssets = useMemo(() => {
        if (!selectedReport || !selectedReport.asset_details) return [];
        return selectedReport.asset_details.filter(asset => {
            const matchesTab = activeDetailTab === 'all' || asset.audited_condition === activeDetailTab;
            const matchesSearch = 
                asset.asset_tag.toLowerCase().includes(detailSearch.toLowerCase()) ||
                asset.product_name.toLowerCase().includes(detailSearch.toLowerCase());
            return matchesTab && matchesSearch;
        });
    }, [selectedReport, activeDetailTab, detailSearch]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const isPicRole = currentUser?.role === 'pic' || currentUser?.role === 'department_pic';

            // Fetch departments (filter by department if PIC)
            let deptsQuery = supabase.from('departments').select('*').order('name');
            if (isPicRole && currentUser?.department_id) {
                deptsQuery = deptsQuery.eq('id', currentUser.department_id);
            }
            const { data: depts, error: deptsError } = await deptsQuery;
            if (deptsError) throw deptsError;

            // Fetch all audit logs with department and auditor info (filter if PIC)
            let logsQuery = supabase
                .from('audit_logs')
                .select(`
                    *,
                    departments!department_id(name),
                    profiles:audited_by(full_name)
                `)
                .order('created_at', { ascending: false });
            if (isPicRole && currentUser?.department_id) {
                logsQuery = logsQuery.eq('department_id', currentUser.department_id);
            }
            const { data: logs, error: logsError } = await logsQuery;
            if (logsError) throw logsError;

            setDepartments(depts || []);
            setAuditLogs(logs || []);
        } catch (error) {
            console.error("Error fetching audit reports:", error);
            addNotification("Failed to load audit reports.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentUser]);

    useEffect(() => {
        if (selectedReport) {
            setActiveDetailTab('all');
            setDetailSearch('');
        }
    }, [selectedReport]);

    // Compliance Matrix Logic: Check for logs in current month
    const complianceMatrix = useMemo(() => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        return departments.map(dept => {
            const monthlyLog = auditLogs.find(log => {
                const logDate = new Date(log.created_at);
                return log.department_id === dept.id &&
                    logDate.getMonth() === currentMonth &&
                    logDate.getFullYear() === currentYear;
            });

            return {
                ...dept,
                status: monthlyLog ? 'Completed' : 'Pending',
                lastLog: monthlyLog
            };
        });
    }, [departments, auditLogs]);

    const handleExportCSV = () => {
        const columns = [
            { key: 'department_name', label: 'Department', value: (row) => row.departments?.name || 'N/A' },
            { key: 'audited_by', label: 'Audited By', value: (row) => row.profiles?.full_name || 'N/A' },
            { key: 'total_assets', label: 'Total Assets' },
            { key: 'good_count', label: 'Good' },
            { key: 'damaged_count', label: 'Damaged' },
            { key: 'missing_count', label: 'Missing' },
            { key: 'created_at', label: 'Date', value: (row) => new Date(row.created_at).toLocaleString() }
        ];

        const success = exportToProfessionalCSV({
            data: auditLogs,
            reportTitle: 'Historical Audit Logs Report',
            fileNamePrefix: 'audit_logs_export',
            columns
        });

        if (success) {
            addNotification("Audit logs exported successfully!", "success");
        }
    };

    const resetAudit = async (logId, departmentName) => {
        const isConfirmed = window.confirm(`⚠️ WARNING: Are you sure you want to delete the current audit for ${departmentName}? \n\nThis will instantly unlock the PIC's dashboard and require them to re-do the entire audit for this month.`);

        if (!isConfirmed) return;

        try {
            const { error } = await supabase
                .from('audit_logs')
                .delete()
                .eq('id', logId);

            if (error) throw error;

            addNotification(`Audit for ${departmentName} has been reset. The PIC can now resubmit.`, "success");
            await fetchData();
        } catch (error) {
            console.error("Error resetting audit:", error);
            addNotification("Failed to reset the audit.", "error");
        }
    };

    if (loading) {
        return <div className="p-8 text-center text-muted-foreground">Loading audit reports...</div>;
    }

    return (
        <div className="space-y-8">
            <NotificationContainer
                notifications={notifications}
                onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
            />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Audit Management</h1>
                    <p className="text-muted-foreground">Monitor departmental compliance and browse historical audit snapshots.</p>
                </div>
                <Button variant="outline" iconName="Download" onClick={handleExportCSV}>
                    Export to CSV
                </Button>
            </div>

            {/* Monthly Compliance Overview */}
            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Icon name="CalendarCheck" size={20} className="text-primary" />
                            <h2 className="text-lg font-bold text-foreground">Monthly Compliance: {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {complianceMatrix.map((dept) => (
                        <div 
                            key={dept.id} 
                            className={`p-5 rounded-xl border shadow-sm transition-all ${
                                dept.status === 'Completed' 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-amber-50 border-amber-200'
                            }`}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="font-bold text-foreground truncate mr-2" title={dept.name}>{dept.name}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                    dept.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {dept.status}
                                </span>
                            </div>

                            {dept.status === 'Completed' ? (
                                <div className="space-y-3">
                                    <div className="flex justify-around text-center">
                                        <div>
                                            <p className="text-[10px] text-muted-foreground font-bold uppercase">Good</p>
                                            <p className="text-sm font-bold text-green-600">{dept.lastLog.good_count}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-muted-foreground font-bold uppercase">Dmg</p>
                                            <p className="text-sm font-bold text-amber-600">{dept.lastLog.damaged_count}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-muted-foreground font-bold uppercase">Miss</p>
                                            <p className="text-sm font-bold text-red-600">{dept.lastLog.missing_count}</p>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-green-200/50 flex justify-between items-end">
                                        <div>
                                            <p className="text-[10px] text-muted-foreground">PIC: <span className="text-foreground font-medium">{dept.lastLog.profiles?.full_name || 'N/A'}</span></p>
                                            <p className="text-[10px] text-muted-foreground">Date: <span className="text-foreground font-medium">{formatDate(dept.lastLog.created_at, preferredFormat)}</span></p>
                                        </div>
                                        {canDelete && (
                                            <button 
                                                onClick={() => resetAudit(dept.lastLog.id, dept.name)}
                                                className="text-[10px] font-bold text-red-600 hover:text-red-700 underline uppercase tracking-tighter transition-colors"
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-4 text-amber-600/60">
                                    <Icon name="Clock" size={32} />
                                    <p className="text-xs font-medium mt-2">Audit Pending</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Historical Reports Browser */}
            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Icon name="History" size={20} className="text-primary" />
                    <h2 className="text-lg font-bold text-foreground">Historical Audit Logs</h2>
                </div>
                <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-border">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Department</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">PIC</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">Total</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">Summary</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-card divide-y divide-border">
                                {auditLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                                            No audit logs found.
                                        </td>
                                    </tr>
                                ) : (
                                    auditLogs.map((log) => (
                                        <tr key={log.id} className="hover:bg-muted transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                                                {formatDate(log.created_at, preferredFormat)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                                {log.departments?.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                {log.profiles?.full_name || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-bold">
                                                {log.total_assets}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex justify-center gap-2">
                                                    <span className="text-xs font-bold text-green-600" title="Good">{log.good_count}G</span>
                                                    <span className="text-xs font-bold text-amber-600" title="Damaged">{log.damaged_count}D</span>
                                                    <span className="text-xs font-bold text-red-600" title="Missing">{log.missing_count}M</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="xs" 
                                                    iconName="ExternalLink" 
                                                    onClick={() => setSelectedReport(log)}
                                                >
                                                    Details
                                                </Button>
                                                {canDelete && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="xs" 
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        iconName="RotateCcw" 
                                                        onClick={() => resetAudit(log.id, log.departments?.name)}
                                                    >
                                                        Reset
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Detail Modal */}
            {selectedReport && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
                        <div className="flex items-center justify-between p-6 border-b border-border bg-card sticky top-0 z-10 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-bold text-foreground">Audit Snapshot Details</h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {selectedReport.departments?.name} - {formatDate(selectedReport.created_at, preferredFormat)}
                                </p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setSelectedReport(null)}>
                                <Icon name="X" size={20} />
                            </Button>
                        </div>
                        <div className="p-6 space-y-6 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-muted p-4 rounded-lg">
                                    <p className="text-xs font-bold text-muted-foreground uppercase">Audited By</p>
                                    <p className="text-sm font-medium mt-1">{selectedReport.profiles?.full_name || 'N/A'}</p>
                                </div>
                                <div className="bg-muted p-4 rounded-lg">
                                    <p className="text-xs font-bold text-muted-foreground uppercase">Total Assets</p>
                                    <p className="text-sm font-medium mt-1">{selectedReport.total_assets}</p>
                                </div>
                            </div>
                            
                            <div className="space-y-3">
                                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Condition Breakdown</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-100">
                                        <div className="flex items-center gap-2">
                                            <Icon name="CheckCircle" size={18} className="text-green-600" />
                                            <span className="text-sm font-medium text-green-900">Good</span>
                                        </div>
                                        <span className="font-bold text-green-600">{selectedReport.good_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-100">
                                        <div className="flex items-center gap-2">
                                            <Icon name="AlertTriangle" size={18} className="text-amber-600" />
                                            <span className="text-sm font-medium text-amber-900">Damaged</span>
                                        </div>
                                        <span className="font-bold text-amber-600">{selectedReport.damaged_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-100">
                                        <div className="flex items-center gap-2">
                                            <Icon name="XCircle" size={18} className="text-red-600" />
                                            <span className="text-sm font-medium text-red-900">Missing</span>
                                        </div>
                                        <span className="font-bold text-red-600">{selectedReport.missing_count}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-border">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">Compliance Percentage</span>
                                    <span className="text-lg font-bold text-primary">
                                        {selectedReport.total_assets ? Math.round((selectedReport.good_count / selectedReport.total_assets) * 100) : 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2 mt-2">
                                    <div 
                                        className="bg-primary h-full rounded-full transition-all" 
                                        style={{ width: `${selectedReport.total_assets ? (selectedReport.good_count / selectedReport.total_assets) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>

                            {/* Detailed Asset List */}
                            <div className="space-y-4 pt-4 border-t border-border">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Asset Details</h3>
                                    <div className="flex flex-wrap gap-1 bg-muted p-1 rounded-lg">
                                        {['all', 'good', 'damaged', 'missing'].map((tab) => {
                                            const count = tab === 'all' 
                                                ? selectedReport.asset_details?.length || 0
                                                : selectedReport.asset_details?.filter(a => a.audited_condition === tab).length || 0;
                                            return (
                                                <button
                                                    key={tab}
                                                    type="button"
                                                    onClick={() => setActiveDetailTab(tab)}
                                                    className={`px-2.5 py-1 rounded-md text-xs font-bold capitalize transition-all ${
                                                        activeDetailTab === tab
                                                            ? 'bg-card text-foreground shadow-sm'
                                                            : 'text-muted-foreground hover:text-foreground'
                                                    }`}
                                                >
                                                    {tab} ({count})
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="relative">
                                    <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search by tag or product..."
                                        value={detailSearch}
                                        onChange={(e) => setDetailSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border border-border bg-card rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>

                                <div className="border border-border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                                    {!selectedReport.asset_details ? (
                                        <div className="p-6 text-center text-sm text-muted-foreground">
                                            No asset details were recorded for this audit snapshot. Detailed breakdown is available for audits starting from May 2026.
                                        </div>
                                    ) : filteredDetailAssets.length === 0 ? (
                                        <div className="p-6 text-center text-sm text-muted-foreground">
                                            No matching assets found.
                                        </div>
                                    ) : (
                                        <table className="min-w-full divide-y divide-border text-left">
                                            <thead className="bg-muted text-xs font-bold text-muted-foreground uppercase tracking-wider sticky top-0">
                                                <tr>
                                                    <th className="px-4 py-2">Asset Tag</th>
                                                    <th className="px-4 py-2">Product Name</th>
                                                    <th className="px-4 py-2">Audited Condition</th>
                                                    <th className="px-4 py-2">Assigned User</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-card divide-y divide-border text-xs">
                                                {filteredDetailAssets.map((asset) => (
                                                    <tr key={asset.asset_tag} className="hover:bg-muted/50 transition-colors">
                                                        <td className="px-4 py-2.5 font-bold">
                                                            <a 
                                                                href={`/assets/${asset.asset_tag}`}
                                                                className="text-[#0057B8] hover:underline"
                                                                onClick={() => setSelectedReport(null)}
                                                            >
                                                                {asset.asset_tag}
                                                            </a>
                                                        </td>
                                                        <td className="px-4 py-2.5 font-medium text-foreground">{asset.product_name}</td>
                                                        <td className="px-4 py-2.5">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase text-[9px] ${
                                                                asset.audited_condition === 'good' ? 'bg-green-100 text-green-700' :
                                                                asset.audited_condition === 'damaged' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-red-100 text-red-700'
                                                            }`}>
                                                                {asset.audited_condition}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2.5 text-muted-foreground">{asset.current_user}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-6 border-t border-border bg-muted rounded-b-xl">
                            <Button variant="outline" onClick={() => setSelectedReport(null)}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditReports;
